/**
 * Collection Infinite Scroll / Load More
 *
 * Custom element that handles both infinite scroll and load more button
 * pagination modes for collection pages.
 *
 * Compatible with facets.js filter system.
 */

class CollectionLoadMore extends HTMLElement {
  constructor() {
    super();

    this.sectionId = this.dataset.sectionId;
    this.paginationType = this.dataset.paginationType;
    this.nextUrl = this.dataset.nextUrl;
    this.currentPage = parseInt(this.dataset.currentPage, 10);
    this.totalPages = parseInt(this.dataset.totalPages, 10);
    this.threshold = parseInt(this.dataset.threshold, 10) || 300;

    this.isLoading = false;
    this.observer = null;

    this.button = this.querySelector(".collection-load-more__button");
    this.spinner = this.querySelector(".collection-load-more__spinner");
    this.sentinel = this.querySelector(".collection-load-more__sentinel");
    this.statusText = this.querySelector(".collection-load-more__status-text");
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    if (this.paginationType === "load_more" && this.button) {
      this.boundHandleLoadMore = this.handleLoadMore.bind(this);
      this.button.addEventListener("click", this.boundHandleLoadMore);
    } else if (this.paginationType === "infinite_scroll" && this.sentinel) {
      this.initIntersectionObserver();
    }

    // Listen for facet filter changes to reinitialize
    this.boundHandleFacetRender = this.handleFacetRender.bind(this);
    document.addEventListener("facets:rendered", this.boundHandleFacetRender);
  }

  cleanup() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.button && this.boundHandleLoadMore) {
      this.button.removeEventListener("click", this.boundHandleLoadMore);
    }

    document.removeEventListener("facets:rendered", this.boundHandleFacetRender);
  }

  /**
   * Handle filter/facet changes from facets.js
   * When filters change, the container is re-rendered and we need to reset
   */
  handleFacetRender(event) {
    // The facets system has re-rendered the grid
    // Our element will be replaced, so this instance will be disconnected
    // and a new instance will be created by the custom element system
  }

  initIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: `0px 0px ${this.threshold}px 0px`,
      threshold: 0,
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.isLoading && this.nextUrl) {
          this.loadMoreProducts();
        }
      });
    }, options);

    this.observer.observe(this.sentinel);
  }

  handleLoadMore(event) {
    event.preventDefault();
    if (!this.isLoading && this.nextUrl) {
      this.loadMoreProducts();
    }
  }

  async loadMoreProducts() {
    if (this.isLoading || !this.nextUrl) return;

    this.isLoading = true;
    this.showLoading();

    try {
      // Build URL with section rendering
      const url = new URL(this.nextUrl, window.location.origin);
      url.searchParams.set("section_id", this.sectionId);

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      this.appendProducts(html);
      this.updateState(html);
    } catch (error) {
      this.showError();
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }

  appendProducts(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Find the product grid in the fetched HTML
    const newProductGrid = doc.querySelector("[data-product-column]");
    const currentProductGrid = document.querySelector(
      "#product-grid [data-product-column]"
    );

    if (!newProductGrid || !currentProductGrid) {
      return;
    }

    // Get new product items (direct children with .col class)
    const newProducts = newProductGrid.querySelectorAll(":scope > .col");

    // Append new products to current grid
    newProducts.forEach((product, index) => {
      // Clone the product to avoid reference issues
      const clonedProduct = product.cloneNode(true);

      // Update animation order for scroll animations
      if (clonedProduct.hasAttribute("data-cascade")) {
        const currentCount = currentProductGrid.querySelectorAll(
          ":scope > .col"
        ).length;
        clonedProduct.style.setProperty(
          "--animation-order",
          currentCount + index + 1
        );
      }
      currentProductGrid.appendChild(clonedProduct);
    });

    // Reinitialize scroll animations if needed
    if (typeof initializeScrollAnimationTrigger === "function") {
      initializeScrollAnimationTrigger();
    }

    // Update status text "You've viewed X of Y results"
    this.updateStatusText();

    // Dispatch event for other components (e.g., compare, quick add)
    document.dispatchEvent(
      new CustomEvent("collection:productsAdded", {
        detail: {
          products: newProducts,
          page: this.currentPage + 1,
        },
      })
    );
  }

  updateStatusText() {
    if (!this.statusText) return;

    const currentProductGrid = document.querySelector("#product-grid [data-product-column]");
    if (!currentProductGrid) return;

    const currentCount = currentProductGrid.querySelectorAll(":scope > .col").length;
    const totalCount = parseInt(this.statusText.dataset.total, 10);

    // Update the text
    const newText = this.getTranslation("viewed_count")
      .replace("{{ current }}", currentCount)
      .replace("{{ total }}", totalCount);

    this.statusText.textContent = newText;
    this.statusText.dataset.current = currentCount;
  }

  updateState(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Find the new load-more element to get updated pagination info
    const newLoadMore = doc.querySelector("collection-load-more");

    if (newLoadMore && newLoadMore.dataset.nextUrl) {
      this.nextUrl = newLoadMore.dataset.nextUrl;
      this.currentPage = parseInt(newLoadMore.dataset.currentPage, 10);

      // Update data attributes
      this.dataset.nextUrl = this.nextUrl;
      this.dataset.currentPage = this.currentPage;
    } else {
      // No more pages - all products loaded
      this.nextUrl = null;
      this.showComplete();
    }

    // Update the product grid's data attributes
    const productGrid = document.querySelector("#product-grid");
    if (productGrid) {
      productGrid.dataset.currentPage = this.currentPage;
    }
  }

  showLoading() {
    if (this.button) {
      this.button.disabled = true;
      this.button.setAttribute("aria-busy", "true");
    }

    if (this.spinner) {
      this.spinner.classList.remove("hidden");
    }

    const buttonText = this.querySelector(".collection-load-more__button-text");
    if (buttonText) {
      buttonText.classList.add("hidden");
    }
  }

  hideLoading() {
    if (this.button) {
      this.button.disabled = false;
      this.button.removeAttribute("aria-busy");
    }

    if (this.spinner) {
      this.spinner.classList.add("hidden");
    }

    const buttonText = this.querySelector(".collection-load-more__button-text");
    if (buttonText) {
      buttonText.classList.remove("hidden");
    }
  }

  showComplete() {
    // All products loaded - hide button/sentinel, show completion message
    if (this.button) {
      this.button.style.display = "none";
    }

    if (this.sentinel) {
      this.sentinel.style.display = "none";
      if (this.observer) {
        this.observer.disconnect();
      }
    }

    // Create completion message if not exists
    let completeEl = this.querySelector(".collection-load-more__complete");
    if (!completeEl) {
      completeEl = document.createElement("div");
      completeEl.className = "collection-load-more__complete";
      completeEl.setAttribute("aria-live", "polite");
      completeEl.innerHTML = `<span>${this.getTranslation("all_products_loaded")}</span>`;
      this.appendChild(completeEl);
    }
    completeEl.style.display = "block";
  }

  showError() {
    // Show error state - could retry
    if (this.button) {
      const buttonText = this.querySelector(
        ".collection-load-more__button-text"
      );
      if (buttonText) {
        buttonText.textContent = this.getTranslation("load_error");
      }
      this.button.disabled = false;
    }
  }

  getTranslation(key) {
    // Translation strings (from window object set in theme.liquid or defaults)
    const translations = {
      all_products_loaded:
        window.collectionStrings?.allProductsLoaded || "All products loaded",
      load_error:
        window.collectionStrings?.loadError ||
        "Error loading products. Click to retry.",
      viewed_count:
        window.collectionStrings?.viewedCount ||
        "You've viewed {{ current }} of {{ total }} results",
    };
    return translations[key] || key;
  }
}

// Define custom element
customElements.define("collection-load-more", CollectionLoadMore);

/**
 * Reinitialize load-more elements after facets render
 * This handles the case where facets.js replaces the entire ProductGridContainer
 */
document.addEventListener("DOMContentLoaded", () => {
  // Set up mutation observer to detect when facets replaces the grid
  const productGridContainer = document.getElementById("ProductGridContainer");

  if (productGridContainer) {
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          // Check if a new collection-load-more element was added
          const newLoadMore = productGridContainer.querySelector(
            "collection-load-more"
          );
          if (newLoadMore && !newLoadMore._initialized) {
            newLoadMore._initialized = true;
            // Element will auto-initialize via connectedCallback
          }
        }
      });
    });

    mutationObserver.observe(productGridContainer, {
      childList: true,
      subtree: true,
    });
  }
});
